import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; // Import useNavigate and useLocation
import { useAppContext } from '../context/AppContext'; // Import useAppContext
import apiService from '../services/api'; // Corrected import path
// Layout import is removed as it's not used directly here

function CompareProgramsPage() {
  const { comparisonTmsIds, setComparisonTmsIds, apiBaseUrl } = useAppContext(); // Add apiBaseUrl and setComparisonTmsIds from context
  const navigate = useNavigate(); // Get navigate function
  const location = useLocation(); // Get location object for URL parameters
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({}); // State for expanded sections

  useEffect(() => {
    let currentTmsIds = comparisonTmsIds;

    // Check URL for 'ids' parameter if context is empty
    if (!currentTmsIds) {
      const params = new URLSearchParams(location.search);
      const urlIds = params.get('ids');
      if (urlIds) {
        currentTmsIds = urlIds;
        setComparisonTmsIds(urlIds); // Update context with IDs from URL
        console.log("TMS IDs found in URL, updating context:", urlIds);
      }
    }

    console.log("CompareProgramsPage useEffect triggered with tmsids:", currentTmsIds); // Debug log

    if (!currentTmsIds) {
      console.log("No TMS IDs found in context or URL for comparison."); // Debug log
      setError('No TMS IDs provided for comparison.');
      setLoading(false);
      return;
    }

    const fetchComparisonData = async () => {
      setLoading(true);
      setError(null);
      // Reset expansion state when new data is fetched
      setExpandedSections({});
      try {
        console.log(`Attempting to fetch comparison data for TMS IDs: ${currentTmsIds}`); // Debug log
        const data = await apiService.comparePrograms(currentTmsIds);
        console.log("Comparison data received:", data); // Debug log
        setComparisonData(data);
      } catch (err) {
        console.error('Error fetching comparison data:', err);
        setError(`Failed to fetch comparison data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchComparisonData();
  }, [comparisonTmsIds, location.search, setComparisonTmsIds]); // Depend on comparisonTmsIds, location.search, and setComparisonTmsIds

  const toggleSectionExpansion = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // --- Helper to get value (adapts slightly) ---
  const getValue = (sectionData, tmsId, field, index = 0) => { // Index defaults to 0 for collapsed view
    const records = sectionData[tmsId];
    let recordToUse = null;

    if (Array.isArray(records)) {
        recordToUse = records[index];
    } else if (index === 0 && typeof records === 'object' && records !== null) {
        // Handle case where data is a single object, not array
        recordToUse = records;
    }

    if (recordToUse && typeof recordToUse === 'object') {
        const value = recordToUse[field];
        // Render boolean values as strings
        if (typeof value === 'boolean') {
          return value ? 'true' : 'false';
        }
        return value === null ? 'null' : value === undefined ? 'undefined' : String(value);
    }
    return 'N/A';
  };
  // --- End Helper ---

  const handleDownloadExcel = () => {
    // Ensure comparisonTmsIds is a non-empty string and apiBaseUrl is a string (even if empty)
    if (comparisonTmsIds && typeof comparisonTmsIds === 'string' && comparisonTmsIds.length > 0 && typeof apiBaseUrl === 'string') {
      const tmsidsString = comparisonTmsIds; // It's already a string from context
      const downloadUrl = `${apiBaseUrl}/compare/download?tmsids=${tmsidsString}`;
      // Trigger download by navigating to the URL
      window.location.href = downloadUrl;
    } else {
      console.warn("No TMS IDs available for download.");
      // Optionally, show a user-facing message
    }
  };

  return (
    // Layout wrapper is removed as Layout component handles rendering this page
    <div className="container mx-auto p-4 h-full overflow-y-auto"> {/* Added h-full and overflow-y-auto */}
      {/* Header with Back Button and Download Button */}
      <div className="flex justify-between items-center mb-4 sticky top-0 bg-gray-100 py-2 z-10 border-b">
        <h1 className="text-2xl font-bold">Program Comparison</h1>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleDownloadExcel}
            disabled={loading || error || !comparisonTmsIds || comparisonTmsIds.length === 0}
            className="px-4 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 disabled:bg-gray-300"
          >
            Download as Excel
          </button>
          {location.state?.fromSearchResults && (
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600"
            >
              {'< Back to Search Results'}
            </button>
          )}
        </div>
      </div>
      {/* End Header */}

      {loading && <p>Loading comparison data...</p>}
      {error && <p className="text-red-500">Error: {error}</p>}
      {comparisonData && (
        <div>
          {/* Render structured comparison tables or expanded views */}
          {Object.entries(comparisonData).map(([sectionKey, sectionData]) => {
            if (!sectionData || typeof sectionData !== 'object' || Object.keys(sectionData).length === 0) {
              return null; // Skip empty sections
            }

            const tmsIds = Object.keys(sectionData);
            if (tmsIds.length === 0) return null; // Skip if no TMS IDs in section

            const isExpanded = expandedSections[sectionKey] || false;
            // Check if *any* TMS ID in this section has multiple records
            const hasMultipleRecords = tmsIds.some(tmsId => Array.isArray(sectionData[tmsId]) && sectionData[tmsId].length > 1);

            // --- Get all unique field names (only needed for collapsed view) ---
            let uniqueFields = [];
            if (!isExpanded) {
              const allFields = new Set();
              tmsIds.forEach(tmsId => {
                const records = sectionData[tmsId];
                if (Array.isArray(records) && records.length > 0 && typeof records[0] === 'object') {
                  Object.keys(records[0]).forEach(field => allFields.add(field));
                } else if (typeof records === 'object' && records !== null && !Array.isArray(records)) {
                   // Handle cases where the value might be a single object instead of array
                   Object.keys(records).forEach(field => allFields.add(field));
                }
              });
              uniqueFields = Array.from(allFields).sort();
            }
            // --- End Get unique field names ---

            return (
              <div key={sectionKey} className="mb-8">
                <h2 className="text-xl font-semibold mb-3 capitalize border-b pb-1 flex justify-between items-center">
                  <span>{sectionKey.replace(/_/g, ' ')}</span>
                  {/* Show toggle button only if there are actually multiple records */}
                  {hasMultipleRecords && (
                    <button
                      onClick={() => toggleSectionExpansion(sectionKey)}
                      className="text-sm font-normal text-blue-600 hover:text-blue-800 underline ml-2 focus:outline-none"
                      aria-expanded={isExpanded} // Accessibility improvement
                    >
                      {isExpanded ? "(Show first record only)" : "(Compare all records)"}
                    </button>
                  )}
                </h2>

                {/* Conditional Rendering: Collapsed Table or Expanded View */}
                {!isExpanded ? (
                  // --- Collapsed View (First Record Comparison Table) ---
                  <div className="overflow-x-auto shadow rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider sticky left-0 bg-gray-100 z-10 w-1/4 border-r">Field</th>
                          {tmsIds.map(tmsId => (
                            <th key={tmsId} className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">{tmsId}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {uniqueFields.map((field, fieldIndex) => {
                          const values = tmsIds.map(tmsId => getValue(sectionData, tmsId, field)); // Index 0 is default
                          // Check if all values are the same (simple check, handles null/undefined)
                          const firstValueStr = values[0];
                          const allSame = values.every(val => val === firstValueStr);

                          return (
                            <tr key={fieldIndex} className={allSame ? '' : 'bg-yellow-50 hover:bg-yellow-100'}>
                              <td className="px-4 py-1.5 text-sm font-medium text-gray-700 sticky left-0 bg-inherit z-10 w-1/4 border-r">{field}</td>
                              {values.map((value, valueIndex) => (
                                <td key={valueIndex} className={`px-4 py-1.5 text-sm text-gray-600 whitespace-pre-wrap break-words ${!allSame ? 'font-semibold text-yellow-900' : ''}`}>
                                  {value}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  // --- Expanded View (Show all records per TMS ID) ---
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {tmsIds.map(tmsId => (
                      <div key={tmsId} className="border rounded-lg p-3 bg-gray-50 shadow-sm">
                        <h3 className="text-md font-semibold mb-2 text-gray-700 border-b pb-1">{tmsId}</h3>
                        {Array.isArray(sectionData[tmsId]) ? (
                          sectionData[tmsId].map((record, index) => (
                            <div key={index} className="mb-3 border-b border-gray-200 pb-3 last:border-b-0 last:pb-0 last:mb-0">
                              <h4 className="text-sm font-medium text-gray-500 mb-1">Record {index + 1}</h4>
                              <pre className="bg-white p-2 rounded overflow-auto text-xs border border-gray-200">
                                {JSON.stringify(record, null, 2)}
                              </pre>
                            </div>
                          ))
                        ) : (
                           // Handle case where sectionData[tmsId] is not an array (e.g., single object)
                           <pre className="bg-white p-2 rounded overflow-auto text-xs border border-gray-200">
                              {JSON.stringify(sectionData[tmsId], null, 2)}
                            </pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CompareProgramsPage;
